import { useState } from 'react'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { routes } from 'constants/routes'
import { accountManagementStrings, commonStrings } from 'constants/strings'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import {
  useAccountTerms,
  useEnableAccountTermsRequirement,
} from 'hooks/useTermsAdmin'
import { toErrorMessage } from 'hooks/utils'
import { formatDateTime } from 'utils'
import { TermsStateBadge } from '../TermsStateBadge'

const strings = accountManagementStrings.terms

/** 顧客へ渡す規約同意URL（アカウントIDや秘密値を含めない） */
export const buildTermsConsentUrl = () =>
  `${window.location.origin}${routes.TERMS_CONSENT}`

/**
 * アカウント単位の「利用規約への同意を必須にする」。運営の動作確認が終わってから ADMIN が ON にし、
 * 保存の成功を確認してから顧客へ資格と規約同意URLを渡す。ON にしても本人の同意は代行しない。
 */
export function AccountTermsSection({ accountId }: { accountId: number }) {
  const terms = useAccountTerms(accountId)
  const enableRequirement = useEnableAccountTermsRequirement()
  const { status: copyStatus, copy } = useCopyToClipboard()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [enabledMessage, setEnabledMessage] = useState<string | undefined>()

  if (!terms.data) {
    return (
      <section className="grid gap-3 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-ink">{strings.sectionTitle}</h2>
        {terms.isError ? (
          <Alert>{toErrorMessage(terms.error, strings.loadFailed)}</Alert>
        ) : (
          <p className="text-sm text-muted">{commonStrings.loading}</p>
        )}
      </section>
    )
  }

  const data = terms.data
  const isStandard = data.role === 'STANDARD'
  const isExempt = data.state === 'EXEMPT'
  const canEnable =
    isStandard &&
    !data.consentRequired &&
    data.enforcementEnabled &&
    data.hasPublishedVersion
  const changedBy = data.consentRequiredAt
    ? `${formatDateTime(data.consentRequiredAt)} ${data.consentRequiredByName ?? ''}`
    : data.consentRequired
      ? strings.migrated
      : commonStrings.noData
  const latestAcceptance = data.latestAcceptance
    ? `${data.latestAcceptance.versionLabel}（${formatDateTime(data.latestAcceptance.acceptedAt)}）`
    : strings.noAcceptance
  const consentUrl = buildTermsConsentUrl()

  const handleEnable = async () => {
    setEnabledMessage(undefined)
    const result = await enableRequirement
      .mutateAsync(accountId)
      .catch(() => undefined)
    setConfirmOpen(false)
    if (result?.consentRequired) setEnabledMessage(strings.enabled)
  }

  return (
    <section className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
      <div className="grid gap-1">
        <h2 className="text-sm font-semibold text-ink">{strings.sectionTitle}</h2>
        <p className="text-xs text-muted">
          {isExempt ? strings.exemptNotice : strings.sectionDescription}
        </p>
      </div>

      {!isStandard && (
        <p className="text-sm text-muted" role="note">
          {strings.adminNotice}
        </p>
      )}

      {isStandard && (
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-[11px] font-semibold text-muted">
              {strings.labels.setting}
            </dt>
            <dd className="mt-1 text-sm font-semibold text-ink">
              {isExempt ? strings.exempt : data.consentRequired ? strings.on : strings.off}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-muted">
              {strings.labels.state}
            </dt>
            <dd className="mt-1">
              <TermsStateBadge state={data.state} />
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-muted">
              {strings.labels.changedBy}
            </dt>
            <dd className="mt-1 text-sm text-ink">{changedBy}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-muted">
              {strings.labels.latestAcceptance}
            </dt>
            <dd className="mt-1 text-sm text-ink">{latestAcceptance}</dd>
          </div>
        </dl>
      )}

      {isStandard && data.consentRequired && !data.enforcementEnabled && (
        <p className="text-xs text-muted" role="note">
          {strings.enforcementOffNotice}
        </p>
      )}

      {enabledMessage && (
        <div
          role="status"
          className="rounded-2xl border border-accent/20 bg-accent-soft px-4 py-3 text-sm text-ink"
        >
          {enabledMessage}
        </div>
      )}

      {enableRequirement.error && (
        <Alert>{toErrorMessage(enableRequirement.error, strings.enableFailed)}</Alert>
      )}

      {isStandard && !data.consentRequired && (
        <div className="grid gap-2">
          {!canEnable && (
            <p className="text-xs text-muted" role="note">
              {strings.unavailableNotice}
            </p>
          )}
          <div className="flex justify-end">
            <Button
              type="button"
              disabled={!canEnable}
              onClick={() => setConfirmOpen(true)}
            >
              {strings.enable}
            </Button>
          </div>
        </div>
      )}

      {isStandard && data.consentRequired && (
        <div className="grid gap-2">
          <span className="text-[11px] font-semibold text-muted">
            {strings.labels.consentUrl}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-xl border border-border/70 bg-surface px-3 py-2 text-xs text-ink">
              {consentUrl}
            </code>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                void copy(consentUrl)
              }}
            >
              {copyStatus === 'success' ? strings.copied : strings.copyUrl}
            </Button>
          </div>
        </div>
      )}

      {isStandard && (
        <div className="grid gap-2">
          <span className="text-[11px] font-semibold text-muted">
            {strings.labels.history}
          </span>
          {data.acceptances.length === 0 ? (
            <p className="text-sm text-muted">{strings.noHistory}</p>
          ) : (
            <ul className="grid gap-1 text-sm text-ink">
              {data.acceptances.map((acceptance) => (
                <li key={`${acceptance.versionId}-${acceptance.acceptedAt}`}>
                  {acceptance.versionLabel}（{formatDateTime(acceptance.acceptedAt)}）
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        textTitle={strings.enableConfirmTitle}
        textMessage={isExempt ? strings.exemptEnableConfirmMessage : strings.enableConfirmMessage}
        textCancel={commonStrings.cancel}
        textConfirm={strings.enableConfirm}
        isLoading={enableRequirement.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          void handleEnable()
        }}
      />
    </section>
  )
}
